import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import { ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Hangar, Obstacle, ObstacleType } from "../../../types";
import { formatNumber, uuidv4 } from "../../../utils";
import { feetToMeters } from "../../../utils/math";
import { OBSTACLE_STYLES } from "../../../widgets/Layout/widgets/Obstacle";
import {
  CircleDetectionResult,
  getCircleGeometry,
  isCircleObstacle,
  Point,
} from "./obstacleUtils";

type Props = {
  hangar: Hangar;
  feetToPixels: number;
  obstacle: Obstacle;
  onDrag: (obstacle: Obstacle) => void;
  onDelete: (obstacle: Obstacle) => void;
  onCopy?: (obstacle: Obstacle) => void;
  isMetric?: boolean;
};

export const DraggableCircleObstacle: React.FC<Props> = ({
  hangar,
  feetToPixels,
  obstacle,
  onDrag,
  onDelete,
  onCopy,
  isMetric = false,
}) => {
  const { t } = useTranslation();
  const svgRef = React.useRef<SVGSVGElement>(null);

  // State for dragging
  const [isDragging, setIsDragging] = React.useState(false);
  const [dragStartPos, setDragStartPos] = React.useState<Point | null>(null);
  const [isResizing, setIsResizing] = React.useState(false);

  // Context menu
  const [contextMenu, setContextMenu] = React.useState<{
    mouseX: number;
    mouseY: number;
  } | null>(null);

  // Extract circle properties from geometry
  const circleInfo: CircleDetectionResult = React.useMemo(
    () => isCircleObstacle(obstacle),
    [obstacle]
  );

  const center = circleInfo.center || { x: obstacle.x + obstacle.width / 2, y: obstacle.y + obstacle.depth / 2 };
  const radius = circleInfo.radius || Math.min(obstacle.width, obstacle.depth) / 2;

  // Get styling based on obstacle type
  const style = OBSTACLE_STYLES[obstacle.type || ObstacleType.OBSTRUCTION];
  const fillColor = style?.backgroundColor || "transparent";
  const strokeColor = style?.outlineColor || "#666";
  const strokeDash = obstacle.type === ObstacleType.NO_TOW_AREA ? "5,5" : undefined;

  // Handle mouse down on circle (start drag)
  const handleCircleMouseDown = (event: React.MouseEvent) => {
    if (event.button !== 0) return; // Left click only

    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;

    const mouseX = (event.clientX - rect.left) / feetToPixels;
    const mouseY = (event.clientY - rect.top) / feetToPixels;

    // Start drag
    setIsDragging(true);
    setDragStartPos({ x: mouseX, y: mouseY });
    event.stopPropagation();
  };

  // Handle mouse down on resize handle
  const handleResizeMouseDown = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (event.button !== 0) return;

    setIsResizing(true);
    setIsDragging(true);
  };

  // Update obstacle with new circle properties
  const updateCircle = (newCenter: Point, newRadius: number) => {
    const geom = getCircleGeometry(newCenter, newRadius);

    onDrag({
      ...obstacle,
      geom,
      x: newCenter.x - newRadius,
      y: newCenter.y - newRadius,
      width: newRadius * 2,
      depth: newRadius * 2,
    });
  };

  // Context menu handlers
  const handleContextMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    setContextMenu(
      contextMenu === null
        ? { mouseX: event.clientX + 2, mouseY: event.clientY - 6 }
        : null
    );
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const handleCopy = () => {
    if (!onCopy) return;

    // Create a copy offset by (5, 5) feet
    const newCenter = { x: center.x + 5, y: center.y + 5 };
    const geom = getCircleGeometry(newCenter, radius);

    const copiedObstacle: Obstacle = {
      ...obstacle,
      id: uuidv4(),
      geom,
      x: newCenter.x - radius,
      y: newCenter.y - radius,
      width: radius * 2,
      depth: radius * 2,
    };

    onCopy(copiedObstacle);
    handleCloseContextMenu();
  };

  const handleDelete = () => {
    onDelete(obstacle);
    handleCloseContextMenu();
  };

  // Add window-level mouse handlers for drag
  React.useEffect(() => {
    if (isDragging) {
      const handleWindowMouseMove = (event: MouseEvent) => {
        const rect = svgRef.current?.getBoundingClientRect();
        if (!rect) return;

        const mouseX = (event.clientX - rect.left) / feetToPixels;
        const mouseY = (event.clientY - rect.top) / feetToPixels;

        if (isResizing) {
          // Calculate new radius from mouse distance to center
          const distance = Math.hypot(mouseX - center.x, mouseY - center.y);
          const newRadius = Math.max(0.5, distance);

          // Ensure circle stays within bounds
          const constrainedRadius = Math.min(
            newRadius,
            center.x,
            center.y,
            hangar.width - center.x,
            hangar.depth - center.y
          );

          updateCircle(center, constrainedRadius);
        } else if (dragStartPos) {
          // Move center
          const dx = mouseX - dragStartPos.x;
          const dy = mouseY - dragStartPos.y;

          // Constrain to hangar bounds
          const newCenterX = Math.max(radius, Math.min(hangar.width - radius, center.x + dx));
          const newCenterY = Math.max(radius, Math.min(hangar.depth - radius, center.y + dy));
          const clampedDx = newCenterX - center.x;
          const clampedDy = newCenterY - center.y;

          if (clampedDx !== 0 || clampedDy !== 0) {
            const newCenter = { x: center.x + clampedDx, y: center.y + clampedDy };
            updateCircle(newCenter, radius);
            setDragStartPos({ x: dragStartPos.x + clampedDx, y: dragStartPos.y + clampedDy });
          }
        }
      };

      const handleWindowMouseUp = () => {
        setIsDragging(false);
        setDragStartPos(null);
        setIsResizing(false);
      };

      window.addEventListener("mousemove", handleWindowMouseMove);
      window.addEventListener("mouseup", handleWindowMouseUp);

      return () => {
        window.removeEventListener("mousemove", handleWindowMouseMove);
        window.removeEventListener("mouseup", handleWindowMouseUp);
      };
    }
  }, [isDragging, isResizing, dragStartPos, center, radius, hangar, feetToPixels]);

  // Format diameter for display
  const diameter = radius * 2;
  const displayDiameter = isMetric
    ? formatNumber(feetToMeters(diameter), 2)
    : formatNumber(diameter, 1);
  const unit = isMetric ? "m" : "ft";

  return (
    <>
      <svg
        ref={svgRef}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: hangar.width * feetToPixels,
          height: hangar.depth * feetToPixels,
          pointerEvents: "none",
          overflow: "visible",
        }}
      >
        {/* Main circle */}
        <circle
          cx={center.x * feetToPixels}
          cy={center.y * feetToPixels}
          r={radius * feetToPixels}
          fill={fillColor}
          stroke={strokeColor}
          strokeWidth={2}
          strokeDasharray={strokeDash}
          style={{
            pointerEvents: "auto",
            cursor: isDragging && !isResizing ? "grabbing" : "grab",
          }}
          onMouseDown={handleCircleMouseDown}
          onContextMenu={handleContextMenu}
        />

        {/* Center point indicator */}
        <circle
          cx={center.x * feetToPixels}
          cy={center.y * feetToPixels}
          r={4}
          fill="white"
          stroke="#333"
          strokeWidth={1}
          style={{ pointerEvents: "none" }}
        />

        {/* Resize handle (on right edge) */}
        <circle
          cx={(center.x + radius) * feetToPixels}
          cy={center.y * feetToPixels}
          r={6}
          fill="white"
          stroke="#333"
          strokeWidth={2}
          style={{
            pointerEvents: "auto",
            cursor: "ew-resize",
          }}
          onMouseDown={handleResizeMouseDown}
        />

        {/* Diameter label */}
        <text
          x={center.x * feetToPixels}
          y={(center.y + radius + 2) * feetToPixels}
          textAnchor="middle"
          dominantBaseline="hanging"
          fill="#333"
          fontSize={12}
          fontWeight="bold"
          style={{
            pointerEvents: "none",
            userSelect: "none",
            textShadow: "0 0 3px white, 0 0 3px white",
          }}
        >
          {displayDiameter} {unit}
        </text>
      </svg>

      {/* Context menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        {onCopy && (
          <MenuItem onClick={handleCopy}>
            <ListItemIcon>
              <ContentCopyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {t("settings.hangarSettings.obstacleContextMenu.copy", "Copy")}
            </ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={handleDelete}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>
            {t("settings.hangarSettings.obstacleContextMenu.delete", "Delete")}
          </ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};
